import { ContentBox } from '../ContentBox';
import { PubReportDefinition } from '../../../generated/hooks';
import { useTranslation } from 'react-i18next';
import {
  BodyMedium,
  BodyXLarge,
  SubtleButton,
  StackedBarChart,
  theme,
  DeprecatedChevronDownIcon,
  DeprecatedChevronUpIcon,
} from '@arnold/common';
import { ReactComponent as ChartResult } from '../../../assets/images/ChartResult.svg';
import styled from '@emotion/styled';
import { Cell, Legend, Pie, PieChart } from 'recharts';
import { getMessagesWithQuestionIndex } from '../../../lib/reportHelper';
import { calculateNPS } from '@arnold/core';
import { IReport } from '../../../lib/reports/base';
import { LegendWithValue } from '@arnold/common/lib/components/charts/CircleGraph/CircleGraph';
import { useState } from 'react';
import { ResponsiveSwitcher } from '../ResponsiveSwitcher';

type NpsWidgetProps = {
  widget: NonNullable<NonNullable<PubReportDefinition['sections']>[0]['widgets']>[0];
  report: IReport;
  chart: React.ReactNode;
};

const TitleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  width: 20%;
`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10%;
`;

const MobileContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  justify-content: space-between;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${theme.spacing.g};
  align-items: center;
`;

export const NpsWidget = ({ widget, report, chart }: NpsWidgetProps) => {
  const { t } = useTranslation('Report');
  const [showDetails, setShowDetails] = useState(false);
  const npsAnswers = getMessagesWithQuestionIndex(report.responses, widget.questionDefinitionIndex || '');
  const answersWithValues = npsAnswers.filter((npsA) => npsA.value !== null && npsA.value !== undefined);
  const detractors = answersWithValues.filter((npsA) => parseInt(npsA.value!, 10) < 7).length;
  const promoters = answersWithValues.filter((npsA) => parseInt(npsA.value!, 10) > 8).length;
  const neutral = answersWithValues.filter(
    (npsA) => parseInt(npsA.value!, 10) >= 7 && parseInt(npsA.value!, 10) <= 8,
  ).length;
  const npsValue = calculateNPS(answersWithValues.map((npsA) => parseInt(npsA.value!, 10)));

  // Transform the range -100 to 100 to be between 0 and 100
  const transformRange = (value: number): number => {
    return (value + 100) / 2;
  };

  const pieChartData = [
    {
      value: 100 - transformRange(npsValue),
      color: theme.colors.actionInvertedPrimary.default,
      backgroundColor: theme.colors.actionPrimary.default,
    },
    {
      value: transformRange(npsValue),
      color: theme.colors.actionPrimary.default,
      backgroundColor: theme.colors.actionInvertedPrimary.default,
    },
  ];

  return (
    <>
      <ResponsiveSwitcher
        mobile={
          <div data-nps-widget data-nps-widget-is-open={showDetails}>
            <ContentBox header={undefined} subHeader={undefined} key={widget.id} disabled={false}>
              <MobileContainer>
                <BodyMedium bold data-icom="nps-widget-title">
                  {t('npsWidgetTitle')}
                </BodyMedium>
                <ButtonContainer>
                  <BodyXLarge bold>{npsValue}</BodyXLarge>
                  <SubtleButton
                    onClick={() => {
                      setShowDetails(!showDetails);
                    }}
                    size="small"
                    leftIcon={showDetails ? <DeprecatedChevronUpIcon /> : <DeprecatedChevronDownIcon />}
                    data-pptx-hide
                    data-nps-widget-toggle-button={true}
                    aria-label={showDetails ? t('hideDetails') : t('showDetails')}
                  />
                </ButtonContainer>
              </MobileContainer>
            </ContentBox>
            {showDetails && chart}
          </div>
        }
        desktop={
          <div data-nps-widget data-nps-widget-is-open={showDetails} data-cy="nps-widget">
            <ContentBox header={undefined} subHeader={undefined} key={widget.id} disabled={false}>
              <Container>
                <TitleContainer>
                  <ChartResult />
                  <BodyXLarge bold data-icom="nps-widget-title">
                    {t('npsWidgetTitle')}
                  </BodyXLarge>
                </TitleContainer>
                <div style={{ width: '40%' }}>
                  <StackedBarChart
                    showLegend
                    options={[
                      {
                        label: t('detractors'),
                        description: t('detractorsDescription'),
                        value: detractors,
                        color: theme.colors.chart.alert[0],
                      },
                      {
                        label: t('neutral'),
                        description: t('neutralDescription'),
                        value: neutral,
                        color: theme.colors.iconText.secondary,
                      },
                      {
                        label: t('promoters'),
                        description: t('promotersDescription'),
                        value: promoters,
                        color: theme.colors.chart.neutral[1],
                      },
                    ]}
                  />
                </div>

                <PieChart width={160} height={160} data-cy="nps-widget-chart">
                  <Pie
                    data={pieChartData}
                    dataKey="value"
                    startAngle={90}
                    endAngle={450}
                    cx="50%"
                    cy="50%"
                    innerRadius={65}
                    outerRadius={80}
                    blendStroke={true}
                  >
                    {pieChartData?.map((d, index) => <Cell key={index} fill={d.color} />)}
                  </Pie>
                  <Legend
                    content={<LegendWithValue value={npsValue} dataCy="nps-widget-chart-legend" />}
                    align={'center'}
                    verticalAlign={'middle'}
                  />
                </PieChart>
              </Container>
            </ContentBox>
            {showDetails && chart}
            <div className="hide-from-export">
              <SubtleButton
                onClick={() => {
                  setShowDetails(!showDetails);
                }}
                data-cy="nps-widget-toggle-button"
                data-nps-widget-toggle-button={true}
                data-nps-widget-is-open={showDetails}
                data-nps-widget-is-closed={!showDetails}
                size="small"
              >
                {showDetails ? t('hideDetails') : t('showDetails')}
              </SubtleButton>
            </div>
          </div>
        }
      />
    </>
  );
};
